import React, { useEffect, useRef, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API, useAuth } from "../context/AuthContext";
import { useCart } from "../context/CartContext";
import BrandMark from "./BrandMark";

export default function Header({ onSearch, searchValue, setSearchValue, hideSearch = false }) {
  const { user, logout, authHeader } = useAuth();
  const { totals } = useCart();
  const [unread, setUnread] = useState(0);
  const [notifUnread, setNotifUnread] = useState(0);
  const [mobileOpen, setMobileOpen] = useState(false);
  const nav = useNavigate();
  const mobileMenuRef = useRef(null);

  useEffect(() => {
    if (!user) { setUnread(0); setNotifUnread(0); return; }
    const fetchUnread = () => axios.get(`${API}/messages/unread-count`, { headers: authHeader() }).then((r) => setUnread(r.data.unread || 0)).catch(() => {});
    const fetchNotifs = () => axios.get(`${API}/notifications/unread-count`, { headers: authHeader() }).then((r) => setNotifUnread(r.data.unread || 0)).catch(() => {});
    fetchUnread(); fetchNotifs();
    const t1 = setInterval(fetchUnread, 30000);
    const t2 = setInterval(fetchNotifs, 20000);
    // Refetch on window focus — catches newly-created bookings the moment
    // the user comes back to the tab (Stripe just confirmed a payment, etc.)
    const onFocus = () => { fetchUnread(); fetchNotifs(); };
    window.addEventListener("focus", onFocus);
    return () => { clearInterval(t1); clearInterval(t2); window.removeEventListener("focus", onFocus); };
  }, [user?.id]);

  // Close the mobile drawer when clicking outside or hitting Escape, and on
  // route changes (handled by re-mount of the destination page).
  useEffect(() => {
    if (!mobileOpen) return;
    const onKey = (e) => { if (e.key === "Escape") setMobileOpen(false); };
    const onClick = (e) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(e.target)) setMobileOpen(false);
    };
    document.addEventListener("keydown", onKey);
    document.addEventListener("mousedown", onClick);
    return () => {
      document.removeEventListener("keydown", onKey);
      document.removeEventListener("mousedown", onClick);
    };
  }, [mobileOpen]);

  const closeMobile = () => setMobileOpen(false);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (onSearch) onSearch(searchValue);
    else nav(`/services?q=${encodeURIComponent(searchValue || "")}`);
  };

  return (
    <header
      data-testid="site-header"
      className="sticky top-0 z-50 border-b backdrop-blur-xl"
      style={{
        background: "var(--gf-nav-bg)",
        borderColor: "var(--gf-border)",
      }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3.5 flex items-center gap-4 sm:gap-6">
        <Link to="/" data-testid="brand-home-link" className="flex items-center gap-2 shrink-0 group">
          <BrandMark size={28} className="group-hover:scale-105 transition-transform" />
          <span className="flex flex-col leading-none">
            <span className="font-display text-xl sm:text-[1.35rem] font-extrabold tracking-tight" style={{ color: "var(--gf-text)" }}>
              BookFreely
            </span>
          </span>
        </Link>

        {!hideSearch && (
          <form onSubmit={handleSubmit} className="hidden md:flex flex-1 max-w-xl">
            <div
              className="flex w-full items-center gap-2 rounded-lg border px-4 py-2 transition-colors focus-within:border-[var(--gf-border-strong)]"
              style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }}
            >
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--gf-text-muted)" }}>
                <circle cx="11" cy="11" r="7" />
                <line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                data-testid="header-search-input"
                value={searchValue || ""}
                onChange={(e) => setSearchValue && setSearchValue(e.target.value)}
                placeholder="Search services or categories..."
                className="bg-transparent outline-none flex-1 text-sm"
                style={{ color: "var(--gf-text)" }}
              />
            </div>
          </form>
        )}

        <nav className="ml-auto flex items-center gap-2 sm:gap-3 text-sm justify-end" style={{ color: "var(--gf-text-soft)" }}>
          <Link to="/services" data-testid="nav-browse-link" className="hidden sm:inline hover:opacity-80">Browse</Link>
          <Link to="/terms" data-testid="nav-terms-link" className="hidden sm:inline opacity-70 hover:opacity-100">Terms</Link>

          {/* Cart icon — visible for customers and signed-out visitors */}
          {(!user || user.role === "customer") && (
            <Link
              to="/cart"
              data-testid="nav-cart-link"
              aria-label="Open cart"
              className="relative px-3 py-2 rounded-full hover:opacity-80"
              style={{ color: "var(--gf-text)" }}
            >
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <circle cx="9" cy="21" r="1" /><circle cx="20" cy="21" r="1" /><path d="M1 1h4l2.7 13.4a2 2 0 0 0 2 1.6h9.7a2 2 0 0 0 2-1.6L23 6H6" />
              </svg>
              {totals.count > 0 && (
                <span data-testid="cart-count-badge" className="absolute -top-1 -right-1 text-[10px] rounded-full px-1.5 py-0.5 font-semibold" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>
                  {totals.count}
                </span>
              )}
            </Link>
          )}

          {user ? (
            <>
              <Link to="/messages" data-testid="nav-messages-link" className="hidden sm:inline-flex relative px-3 py-2 rounded-full hover:opacity-80 items-center" style={{ color: "var(--gf-text)" }}>
                Messages
                {unread > 0 && (
                  <span data-testid="messages-unread-badge" className="absolute -top-1 -right-1 text-[10px] rounded-full px-1.5 py-0.5 font-semibold" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>{unread}</span>
                )}
              </Link>
              <Link to="/notifications" data-testid="nav-notifications-link" title="Notifications" className="relative px-3 py-2 rounded-full hover:opacity-80" style={{ color: "var(--gf-text)" }}>
                <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true" style={{ display: "inline-block", verticalAlign: "-3px" }}>
                  <path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" />
                  <path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" />
                </svg>
                {notifUnread > 0 && (
                  <span data-testid="notifications-unread-badge" className="absolute -top-1 -right-1 text-[10px] rounded-full px-1.5 py-0.5 font-semibold" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>{notifUnread}</span>
                )}
              </Link>
              {user.role === "super_admin" ? (
                <Link to="/admin" data-testid="nav-admin-dash-link" className="hidden sm:inline-flex px-4 py-2 rounded-full border hover:opacity-80" style={{ borderColor: "var(--gf-accent)", color: "var(--gf-accent)" }}>
                  Super admin
                </Link>
              ) : user.role === "business" ? (
                <Link to="/business" data-testid="nav-business-dash-link" className="hidden sm:inline-flex px-4 py-2 rounded-full border hover:opacity-80" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>Dashboard</Link>
              ) : (
                <Link to="/account" data-testid="nav-customer-dash-link" className="hidden sm:inline-flex px-4 py-2 rounded-full border hover:opacity-80" style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}>My Bookings</Link>
              )}
              <Link
                to="/account/settings"
                data-testid="nav-settings-gear"
                aria-label="Settings"
                title="Settings"
                className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full border hover:opacity-90"
                style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09a1.65 1.65 0 0 0-1-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09a1.65 1.65 0 0 0 1.51-1 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
                </svg>
              </Link>
              <button data-testid="logout-btn" onClick={() => { logout(); nav("/"); }} className="hidden sm:inline text-sm opacity-60 hover:opacity-100" style={{ color: "var(--gf-text)" }}>Log out</button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login-link" className="hidden sm:inline hover:opacity-80 whitespace-nowrap" style={{ color: "var(--gf-text)" }}>Sign in</Link>
              <Link
                to="/signup"
                data-testid="nav-signup-link"
                className="hidden sm:inline px-4 py-2 rounded-full font-medium transition-colors whitespace-nowrap"
                style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
              >
                Get started
              </Link>
            </>
          )}

          {/* Mobile hamburger — only rendered below sm breakpoint */}
          <button
            type="button"
            data-testid="mobile-menu-toggle"
            aria-label={mobileOpen ? "Close menu" : "Open menu"}
            aria-expanded={mobileOpen}
            onClick={(e) => { e.stopPropagation(); setMobileOpen((v) => !v); }}
            className="sm:hidden inline-flex items-center justify-center w-9 h-9 rounded-full border"
            style={{ borderColor: "var(--gf-border-strong)", color: "var(--gf-text)" }}
          >
            {mobileOpen ? (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
            ) : (
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><line x1="3" y1="6" x2="21" y2="6" /><line x1="3" y1="12" x2="21" y2="12" /><line x1="3" y1="18" x2="21" y2="18" /></svg>
            )}
          </button>
        </nav>
      </div>

      {/* Mobile drawer */}
      {mobileOpen && (
        <div
          ref={mobileMenuRef}
          data-testid="mobile-menu-drawer"
          className="sm:hidden border-t"
          style={{ background: "var(--gf-bg)", borderColor: "var(--gf-border)" }}
        >
          <div className="max-w-7xl mx-auto px-4 py-3 flex flex-col gap-1 text-sm" style={{ color: "var(--gf-text)" }}>
            {!hideSearch && (
              <form onSubmit={(e) => { handleSubmit(e); closeMobile(); }} className="mb-2">
                <div
                  className="flex items-center gap-2 rounded-lg border px-3 py-2"
                  style={{ background: "var(--gf-surface)", borderColor: "var(--gf-border)" }}
                >
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" style={{ color: "var(--gf-text-muted)" }}>
                    <circle cx="11" cy="11" r="7" />
                    <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  </svg>
                  <input
                    data-testid="mobile-header-search-input"
                    value={searchValue || ""}
                    onChange={(e) => setSearchValue && setSearchValue(e.target.value)}
                    placeholder="Search services…"
                    className="bg-transparent outline-none flex-1 text-sm"
                    style={{ color: "var(--gf-text)" }}
                  />
                </div>
              </form>
            )}

            <Link data-testid="mobile-nav-browse" to="/services" onClick={closeMobile} className="px-3 py-2 rounded-lg hover:opacity-80">Browse services</Link>

            {user ? (
              <>
                <Link data-testid="mobile-nav-messages" to="/messages" onClick={closeMobile} className="px-3 py-2 rounded-lg hover:opacity-80 flex items-center justify-between">
                  <span>Messages</span>
                  {unread > 0 && <span className="text-[10px] rounded-full px-2 py-0.5 font-semibold" style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}>{unread}</span>}
                </Link>
                {user.role === "super_admin" && (
                  <Link data-testid="mobile-nav-admin" to="/admin" onClick={closeMobile} className="px-3 py-2 rounded-lg hover:opacity-80" style={{ color: "var(--gf-accent)" }}>Super admin</Link>
                )}
                {user.role === "business" && (
                  <Link data-testid="mobile-nav-business" to="/business" onClick={closeMobile} className="px-3 py-2 rounded-lg hover:opacity-80">Business dashboard</Link>
                )}
                {user.role === "customer" && (
                  <Link data-testid="mobile-nav-account" to="/account" onClick={closeMobile} className="px-3 py-2 rounded-lg hover:opacity-80">My bookings</Link>
                )}
                <Link data-testid="mobile-nav-settings" to="/account/settings" onClick={closeMobile} className="px-3 py-2 rounded-lg hover:opacity-80">Settings</Link>
              </>
            ) : (
              <>
                <Link data-testid="mobile-nav-login" to="/login" onClick={closeMobile} className="px-3 py-2 rounded-lg hover:opacity-80">Sign in</Link>
                <Link
                  data-testid="mobile-nav-signup"
                  to="/signup"
                  onClick={closeMobile}
                  className="px-3 py-2 rounded-lg text-center font-medium"
                  style={{ background: "var(--gf-accent)", color: "var(--gf-on-accent)" }}
                >
                  Get started
                </Link>
              </>
            )}

            <div className="border-t mt-2 pt-2 flex flex-wrap gap-x-4 gap-y-1 text-xs" style={{ borderColor: "var(--gf-border)", color: "var(--gf-text-muted)" }}>
              <Link data-testid="mobile-nav-about" to="/about" onClick={closeMobile} className="hover:underline">About</Link>
              <Link data-testid="mobile-nav-help" to="/help" onClick={closeMobile} className="hover:underline">Help</Link>
              <Link data-testid="mobile-nav-terms" to="/terms" onClick={closeMobile} className="hover:underline">Terms</Link>
              <Link data-testid="mobile-nav-privacy" to="/privacy" onClick={closeMobile} className="hover:underline">Privacy</Link>
              <Link data-testid="mobile-nav-cookies" to="/cookies" onClick={closeMobile} className="hover:underline">Cookies</Link>
            </div>

            {user && (
              <button
                data-testid="mobile-nav-logout"
                onClick={() => { closeMobile(); logout(); nav("/"); }}
                className="mt-2 text-left px-3 py-2 rounded-lg hover:opacity-80 text-sm"
                style={{ color: "var(--gf-text-muted)" }}
              >
                Log out
              </button>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
